import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import io from "socket.io-client";
import { FaSearch, FaCheckCircle, FaLightbulb, FaUsers, FaBolt } from "react-icons/fa";
import API_URL from '../../../utils/api';
import "./TextChatLobby.css";
const EQ_FACTOIDS = [
  {
    title: "MISSING NON-VERBAL CUES",
    fact: "Pay attention to the other person's Non-Verbal Cues like body language, facial expressions, tone of voice etc."
  },
  {
    title: "BEING IMPULSIVE",
    fact: "Think before you speak. Think before you act. Don't make insensitive comments. Control your actions."
  },
  {
    title: "LACKING SELF AWARENESS",
    fact: "Pay attention to your own feelings. Recognize what you are feeling, Recognize where the emotion came from, what triggered it. Choose carefully a suitable response behavior. Practice self-awareness."
  },
  {
    title: "BEING UNABLE TO HANDLE CRITICISM",
    fact: "See feedback as an opportunity for personal growth. Do not see it as a personal attack."
  },
  {
    title: "FAILURE TO MANAGE YOUR OWN EMOTIONS",
    fact: "Practice emotional regulation. Don't allow your emotions to dictate your actions. Don't lose your temper."
  },
  {
    title: "SUPPRESSING YOUR OWN EMOTIONS",
    fact: "Acknowledge what you are feeling, whether it is positive or negative. Deal with the feeling. Do not argue with it. Process, not suppress."
  },
  {
    title: "EXPECTING OTHER PEOPLE TO SUPPRESS THEIR OWN EMOTIONS",
    fact: "Recognize and respect that other people have their own emotions. Feelings are an inevitable part of human experience."
  },
  {
    title: "GIVING THE WRONG INTERPRETATION TO OTHER PEOPLE'S EMOTIONS",
    fact: "Recognize that other people experience complex emotions. Listen actively. Ask questions. Try to understand. Try to express compassion."
  },
  {
    title: "NOT PRACTISING EMPATHY BEHAVIOR",
    fact: "Empathy is about understanding and sharing other people's point of view and their feelings."
  },
  {
    title: "TRYING TO FIX A FRIEND'S FEELINGS WITH LOGIC",
    fact: "Be a witness to what your friend is feeling. Don't rush in with some solution that seems logical to you."
  },
  {
    title: "SHUTTING DOWN SOMEONE'S FEELINGS",
    fact: "Try to understand their point of view. Acknowledge what they are feeling. Don't say 'Get over it' or 'You're being too sensitive'. Respect what the other person is feeling. Express compassion."
  },
  {
    title: "TELLING THE OTHER PERSON WHAT THEY SHOULD DO",
    fact: "Listen actively to the other person. Engage with what they are feeling. Don't spit out your solutions to their situation like a candy vending machine gone crazy."
  },
  {
    title: "HIJACKING THE OTHER PERSON'S EMOTIONS",
    fact: "Do not say to the other person when they are being vulnerable 'You never listen to me' or 'You always cry when' or 'There you go again'. Recognize and respect."
  }
];
export default function TextChatLobby() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentFactoid, setCurrentFactoid] = useState(0);
  const [dots, setDots] = useState("");
  const [matchFound, setMatchFound] = useState(false);
  const socketRef = useRef(null);
  
  // Get userId and gender preference
  const userId = sessionStorage.getItem('userId') || localStorage.getItem('userId');
  const genderPreference = location.state?.genderPreference || 'random'; // Default to random
  // Rotate factoids every 8 seconds
  useEffect(() => {
    const factoidTimer = setInterval(() => {
      setCurrentFactoid((prev) => (prev + 1) % EQ_FACTOIDS.length);
    }, 8000);
    return () => clearInterval(factoidTimer);
  }, []);
  // Animate search dots
  useEffect(() => {
    const dotsTimer = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
    }, 500);
    return () => clearInterval(dotsTimer);
  }, []);
  // Join queue and check for matches
  useEffect(() => {
    if (!userId) {
      navigate("/dashboard");
      return;
    }
    // Check if socket already exists and is connected
    if (socketRef.current && socketRef.current.connected) {
      return;
    }
    let isActive = true;
    let pollInterval = null;
    // Initialize socket connection
    const socket = io(API_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      forceNew: true,
      timeout: 10000,
      autoConnect: true
    });
    socketRef.current = socket;
    // Force immediate connection
    if (!socket.connected) {
      socket.connect();
    }
    socket.on('connect', () => {
      // Register for matchmaking after connection is established
      socket.emit('funtellime-register-matching', { userId: parseInt(userId) });
    });
    socket.on('connecting', () => {
      });
    socket.on('connect_error', (error) => {
      });
    socket.on('connect_timeout', () => {
      });
    socket.on('reconnect_attempt', (attemptNumber) => {
      });
    socket.on('disconnect', (reason) => {
      });
    // Listen for match notifications
    socket.on('funtellime-match-found', (data) => {
      if (isActive) {
        console.log('[TextChatLobby] Match found via socket:', data);
        setMatchFound(true);
        setTimeout(() => {
          navigate(`/funtellime/profiles/${data.chatId}`, {
            state: {
              chatId: data.chatId,
              partner: data.partner,
              yourProfile: data.yourProfile,
              conversationStarter: data.conversationStarter,
              similarities: data.similarities
            }
          });
        }, 1000);
      }
    });
    // Join the queue
    const joinQueue = async () => {
      try {
        const response = await fetch(`${API_URL}/api/funtellime/join-queue`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            userId: parseInt(userId),
            genderPreference: genderPreference 
          })
        });
        const data = await response.json();
        if (data.matched) {
          // Match found immediately!
          if (isActive) {
            setMatchFound(true);
            setTimeout(() => {
              navigate(`/funtellime/profiles/${data.chatId}`, {
                state: {
                  chatId: data.chatId,
                  partner: data.partner,
                  yourProfile: data.yourProfile,
                  conversationStarter: data.conversationStarter,
                  similarities: data.similarities
                }
              });
            }, 1000);
          }
          return; // Exit early, don't set up polling
        }
        // No immediate match, start polling
        pollInterval = setInterval(async () => {
          if (!isActive) {
            clearInterval(pollInterval);
            return;
          }
          try {
            const checkResponse = await fetch(
              `${API_URL}/api/funtellime/check-match/${userId}`
            );
            const checkData = await checkResponse.json();
            if (checkData.matched && isActive) {
              clearInterval(pollInterval);
              setMatchFound(true);
              setTimeout(() => {
                navigate(`/funtellime/profiles/${checkData.chatId}`, {
                  state: {
                    chatId: checkData.chatId,
                    partner: checkData.partner,
                    yourProfile: checkData.yourProfile,
                    conversationStarter: checkData.conversationStarter,
                    similarities: checkData.similarities
                  }
                });
              }, 1000);
            }
          } catch (error) {
            }
        }, 3000); // Poll every 3 seconds
      } catch (error) {
        }
    };
    joinQueue();
    return () => {
      isActive = false;
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      
      // Remove from queue via HTTP
      fetch(`${API_URL}/api/funtellime/cancel-queue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: parseInt(userId) })
      }).catch(err => {});
      
      // Also cancel via socket
      if (socketRef.current && socketRef.current.connected) {
        socketRef.current.emit('funtellime-cancel-matching', { userId: parseInt(userId) });
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [userId, navigate, genderPreference]);
  const handleCancel = async () => {
    // Remove from queue
    await fetch(`${API_URL}/api/funtellime/cancel-queue`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: parseInt(userId) })
    }).catch(err => {});
    
    navigate("/dashboard");
  };
  return (
    <div className="text-lobby-container">
      <div className="text-lobby-content">
        {/* Search Status */}
        <div className="search-status">
          <div className="search-animation">
            <div className="pulse-ring"></div>
            <div className="pulse-ring delay-1"></div>
            <div className="pulse-ring delay-2"></div>
            <div className="search-icon">{matchFound ? <FaCheckCircle /> : <FaSearch />}</div>
          </div>
          <h1>{matchFound ? "Match Found!" : `Finding Your Match${dots}`}</h1>
          <p>{matchFound ? "Preparing your chat..." : "Scanning profiles for compatible partners"}</p>
        </div>
        {/* EQ Factoid Popup */}
        <div className="factoid-card">
          <div className="factoid-header">
            <span className="bulb-icon"><FaLightbulb /></span>
            <span className="factoid-label">Emotional Intelligence Tip</span>
          </div>
          <h3>{EQ_FACTOIDS[currentFactoid].title}</h3>
          <p>{EQ_FACTOIDS[currentFactoid].fact}</p>
          <div className="factoid-progress">
            {EQ_FACTOIDS.map((_, index) => (
              <div
                key={index}
                className={`progress-dot ${index === currentFactoid ? "active" : ""}`}
              />
            ))}
          </div>
        </div>
        {/* Status Info */}
        <div className="lobby-info">
          <div className="info-item">
            <span className="info-icon"><FaUsers /></span>
            <span>Matching based on shared interests</span>
          </div>
          <div className="info-item">
            <span className="info-icon"><FaBolt /></span>
            <span>Average wait time: 30 seconds</span>
          </div>
        </div>
        {/* Cancel Button */}
        <button className="cancel-btn" onClick={handleCancel}>
          Cancel Search
        </button>
      </div>
    </div>
  );
}
